import Phaser from 'phaser';
import { TileData } from '../socket/SocketManager';

const TILE_WIDTH = 64;
const TILE_HEIGHT = 32;

const TILE_COLORS: Record<string, number> = {
  ROAD: 0x444444,
  SIDEWALK: 0x888888,
  GROUND: 0x5a8a3c,
  PARK: 0x4caf50,
};

export class IsoMap {
  private tileGraphics: Phaser.GameObjects.Graphics;

  constructor(
    private scene: Phaser.Scene,
    private tiles: TileData[],
    private gridWidth: number,
    private gridHeight: number,
  ) {
    this.tileGraphics = scene.add.graphics();
    this.tileGraphics.setDepth(-1);
    this.drawTiles();
  }

  private drawTiles(): void {
    for (const tile of this.tiles) {
      const { x, y } = this.gridToIso(tile.gridX, tile.gridY);
      const color = TILE_COLORS[tile.tileType] ?? 0x5a8a3c;
      this.drawIsoDiamond(x, y, color);
    }
  }

  gridToIso(gridX: number, gridY: number): { x: number; y: number } {
    return {
      x: (gridX - gridY) * (TILE_WIDTH / 2),
      y: (gridX + gridY) * (TILE_HEIGHT / 2),
    };
  }

  isoToGrid(isoX: number, isoY: number): { gridX: number; gridY: number } {
    return {
      gridX: Math.floor(isoY / TILE_HEIGHT + isoX / TILE_WIDTH),
      gridY: Math.floor(isoY / TILE_HEIGHT - isoX / TILE_WIDTH),
    };
  }

  private drawIsoDiamond(cx: number, cy: number, color: number): void {
    const hw = TILE_WIDTH / 2;
    const hh = TILE_HEIGHT / 2;

    this.tileGraphics.fillStyle(color, 1);
    this.tileGraphics.beginPath();
    this.tileGraphics.moveTo(cx, cy - hh);
    this.tileGraphics.lineTo(cx + hw, cy);
    this.tileGraphics.lineTo(cx, cy + hh);
    this.tileGraphics.lineTo(cx - hw, cy);
    this.tileGraphics.closePath();
    this.tileGraphics.fillPath();

    // Outline
    this.tileGraphics.lineStyle(1, 0x333333, 0.3);
    this.tileGraphics.beginPath();
    this.tileGraphics.moveTo(cx, cy - hh);
    this.tileGraphics.lineTo(cx + hw, cy);
    this.tileGraphics.lineTo(cx, cy + hh);
    this.tileGraphics.lineTo(cx - hw, cy);
    this.tileGraphics.closePath();
    this.tileGraphics.strokePath();
  }

  getWorldBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    // For a 30x30 iso grid:
    // Top-left corner:  gridToIso(0, gridH-1)   → leftmost point
    // Top-right corner: gridToIso(gridW-1, 0)   → rightmost point, topmost Y
    // Bottom corner:    gridToIso(gridW-1, gridH-1) → bottommost Y
    // Origin corner:    gridToIso(0, 0)          → topmost Y (y=0)
    const origin = this.gridToIso(0, 0);
    const left = this.gridToIso(0, this.gridHeight - 1);
    const right = this.gridToIso(this.gridWidth - 1, 0);
    const bottom = this.gridToIso(this.gridWidth - 1, this.gridHeight - 1);

    return {
      minX: left.x - TILE_WIDTH,
      minY: origin.y - 100,    // 100px for tallest building (casino=80 + margin)
      maxX: right.x + TILE_WIDTH,
      maxY: bottom.y + TILE_HEIGHT * 2,
    };
  }

  getTileWidth(): number {
    return TILE_WIDTH;
  }

  getTileHeight(): number {
    return TILE_HEIGHT;
  }
}
